// typed API client. talks only to the same-origin worker (/api/*). in local dev it
// attaches an X-Dev-Email header (the worker honours it ONLY in development); in
// production Cloudflare Access supplies the identity and no header is sent.

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, message: string, code = 'error') {
    super(message);
    this.status = status;
    this.code = code;
    this.name = 'ApiError';
  }
}

export const DEV_EMAIL_KEY = 'devEmail';

function devHeaders(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  const email = localStorage.getItem(DEV_EMAIL_KEY) || 'admin@technohull.com';
  return { 'X-Dev-Email': email };
}

// When the session has expired (e.g. overnight), reload the page once so sign-in re-runs. Under
// Cloudflare Access this re-authenticates transparently while the upstream Google session is still
// valid, so the app comes back signed in on its own without the user doing anything. Guarded by a
// short-lived flag so a genuinely signed-out user (still unauthorized right after the reload) falls
// through to the Login screen instead of looping.
const AUTH_RELOAD_KEY = 'rolodex_auth_reload_at';
function reauthOnce(): boolean {
  if (import.meta.env.DEV) return false; // dev auth is a header, never reload-loop
  try {
    const last = Number(sessionStorage.getItem(AUTH_RELOAD_KEY) || '0');
    if (Date.now() - last < 20_000) return false; // just reloaded and still unauthorized → show Login
    sessionStorage.setItem(AUTH_RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage blocked (private mode): allow a single reload attempt anyway */
  }
  window.location.reload();
  return true;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...devHeaders(),
      ...(init?.headers ?? {}),
    },
  });

  // Session expired: either the worker says 401, or the platform (Cloudflare Access) redirected the
  // API call to its own sign-in page on another origin. In both cases reload to re-authenticate.
  let crossOriginRedirect = false;
  if (res.redirected) {
    try { crossOriginRedirect = new URL(res.url).origin !== window.location.origin; } catch { /* ignore */ }
  }
  if (res.status === 401 || crossOriginRedirect) {
    if (reauthOnce()) return new Promise<T>(() => {}); // never resolves; the page is reloading
    throw new ApiError(401, 'Your session has expired. Please sign in again.', 'unauthorized');
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      // Not JSON — most likely an auth/login HTML page served in place of our API response, which
      // means the session lapsed. Reload to re-authenticate rather than surfacing a parse error.
      if (reauthOnce()) return new Promise<T>(() => {});
      throw new ApiError(res.status || 502, 'Unexpected response from the server.', 'bad_response');
    }
  }
  if (!res.ok) {
    const body = data as { error?: string; code?: string } | null;
    throw new ApiError(res.status, body?.error ?? res.statusText, body?.code ?? 'error');
  }
  return data as T;
}

/** upload a raw body with progress (0..1) through XMLHttpRequest, which fetch can't report on. Same headers
 *  and error shape as request(); an abort rejects with an AbortError. */
function uploadWithProgress<T>(path: string, body: Blob, contentType: string, opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (opts.signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.setRequestHeader('content-type', contentType);
    for (const [k, v] of Object.entries(devHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total) opts.onProgress?.(e.loaded / e.total); };
    xhr.onload = () => {
      if (xhr.status === 401 && reauthOnce()) return; // the page is reloading
      let data: unknown = null;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { data = null; }
      if (xhr.status >= 200 && xhr.status < 300 && data) return resolve(data as T);
      const b = data as { error?: string; code?: string } | null;
      const fallback = xhr.status === 401 ? 'Your session has expired. Please sign in again.' : xhr.status === 413 ? 'This file is too large.' : 'The upload failed.';
      reject(new ApiError(xhr.status || 502, b?.error ?? fallback, b?.code ?? 'error'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'The upload failed - check your connection.', 'network'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

/** a file the worker serves (an .xlsx kept in R2), as bytes, with the same headers and sign-in handling as
 *  request(); a refusal comes back as an ApiError with the worker's message */
async function bytes(path: string): Promise<Uint8Array<ArrayBuffer>> {
  const res = await fetch(path, { credentials: 'same-origin', headers: devHeaders() });
  if (res.status === 401) {
    if (reauthOnce()) return new Promise<Uint8Array<ArrayBuffer>>(() => {});
    throw new ApiError(401, 'Your session has expired. Please sign in again.', 'unauthorized');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
    throw new ApiError(res.status, body?.error ?? res.statusText, body?.code ?? 'error');
  }
  return new Uint8Array(await res.arrayBuffer());
}

/** a small JSON POST that still goes out while the page is closing or reloading (a keepalive fetch, with the
 *  same headers as request()). Best effort: nothing comes back and it never throws. */
function beacon(path: string, body: unknown): void {
  try {
    void fetch(path, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...devHeaders() },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    /* the page is going anyway */
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  /** upload a raw body (e.g. a File) — used by the R2 document manager. */
  upload: <T>(path: string, body: BodyInit, contentType: string) =>
    request<T>(path, { method: 'POST', body, headers: { 'content-type': contentType } }),
  /** upload a raw body and report its progress (chat attachments). */
  uploadWithProgress,
  bytes,
  beacon,
};
